<template>

  <!-- XOL-67 follow-up (decisions 193 to 202): the "Site handover" block of the Step 3 sharer form (LicenseeForm is the only host, and only
       for an RTS that an RTA's Complete opened). It has no el-form of its own: it sits inside the host's. The host decides when it shows
       and owns the state (`value`, edited in place), the errors and the refusal; this component draws them. -->
  <div class="site-transition-wrap">

    <!-- A refusal replaces the whole group, in the same slot. -->
    <el-alert
      v-if="refusal"
      key="site-transition-refused"
      class="site-transition__refused"
      type="warning"
      show-icon
      :closable="false"
      :title="refusal.title"
      :description="refusal.description" />

    <!-- tabindex -1: a blocked save focuses the GROUP (focusGroup), never a date input, which would open Element's calendar over the message. -->
    <div
      v-else
      key="site-transition-group"
      ref="group"
      :class="['site-transition', focused ? 'is-focused' : '']"
      role="group"
      :aria-labelledby="captionId"
      :aria-describedby="errorText ? errorId : null"
      tabindex="-1"
      @blur="focused = false">

      <small :id="captionId" class="site-transition__caption">Site handover</small>
      <!-- Element's own message under a field has no id and no role, so nothing says it aloud: this copy is what the group points at, only while there is a message. -->
      <span v-if="errorText" :id="errorId" class="site-transition__sr">{{ errorText }}</span>
      <span class="site-transition__sr" aria-live="polite">{{ live }}</span>

      <check-box no-form v-model="value.on" :disabled="disabled" @change="onToggle">{{ mode === 'modify' ? 'Update the site\'s handover date and status' : 'Set the site\'s handover date and status' }}</check-box>

      <small class="site-transition__hint">
        <template v-if="siteHasDate && stored">{{ siteLabel }} is a Candidate site. Its handover is set for <b>{{ formatDate(site.handover) }}</b>, becoming <b>{{ stored }}</b>.</template>
        <template v-else-if="siteHasDate">{{ siteLabel }} is a Candidate site. Its handover is set for <b>{{ formatDate(site.handover) }}</b>, with no status yet.</template>
        <template v-else-if="stored">{{ siteLabel }} is a Candidate site. Its handover status is <b>{{ stored }}</b>, with no date yet.</template>
        <template v-else>{{ siteLabel }} is a Candidate site with no handover date.</template>
      </small>

      <!-- Mounted only while ticked: the status select copies its value once, when it is created (XOL-552), and the toggle has set it by then. -->
      <template v-if="value.on">

        <small class="site-transition__hint">On the site handover date, {{ siteLabel }} changes from Candidate to the status below.</small>

        <div class="site-transition__sub">
          <check-box no-form v-model="value.followSharer" :disabled="disabled" @change="onFollow">Same as the sharer's Handover Date</check-box>
        </div>

        <el-row :gutter="12" type="flex" class="site-transition__grid">

          <el-col :xs="24" :sm="12">
            <el-form-item v-if="value.followSharer" key="site-date-follow" label="Site handover date" required :error="errors.handover || null">
              <b v-if="value.handover">{{ formatDate(value.handover) }}</b>
              <span v-else class="site-transition__empty">Set the sharer's Handover Date above.</span>
              <small v-if="dateCue" class="site-transition__hint site-transition__hint--cue">{{ dateCue }}</small>
            </el-form-item>

            <date-input
              v-else
              key="site-date-pick"
              v-model="value.handover"
              label="Site handover date"
              aria-label="Site handover date"
              placeholder="Pick a date"
              required
              :disabled="disabled"
              :error="errors.handover || null"
              @change="$emit('change')">
              <template #cue><small v-if="dateCue" class="site-transition__hint site-transition__hint--cue">{{ dateCue }}</small></template>
            </date-input>
          </el-col>

          <el-col :xs="24" :sm="12">
            <select-site-status
              key="site-status"
              v-model="value.handoverStatus"
              label="Site handover status"
              aria-label="Site handover status"
              placeholder="Select a status"
              :only="statuses"
              required
              :disabled="disabled"
              :error="errors.handoverStatus || null"
              @change="$emit('change')">
              <template #cue><small v-if="statusCue" class="site-transition__hint site-transition__hint--cue">{{ statusCue }}</small></template>
            </select-site-status>
          </el-col>

        </el-row>

        <small v-if="same" class="site-transition__hint site-transition__hint--note">These are the site's current handover date and status, so saving won't change the site.</small>

        <!-- Decision 86: a date of today or earlier only warns; the nightly run, never the save, moves the site. -->
        <el-alert
          v-else-if="warning"
          key="site-handover-warning"
          class="site-transition__warning"
          type="warning"
          show-icon
          :closable="false"
          :title="warning.title"
          :description="warning.description" />

      </template>

    </div>

  </div>

</template>


<script>

import DateMixins from '@/mixins/DateMixins'
import { handoverWarning, handoverWarningCopy } from '@/lib/rtaCompletion'
import { appNowDateSql } from '@/lib/appTime'
import { STATUSES, follow, storedStatus, sameAsSite, ymd } from '@/lib/siteTransition'

import CheckBox from '@/components/atoms/inputs/CheckBox'
import DateInput from '@/components/atoms/inputs/DateInput'
import SelectSiteStatus from '@/components/atoms/selects/SelectSiteStatus'

export default {
  name: 'SiteTransitionFields',

  mixins: [DateMixins],

  components: {
    CheckBox,
    DateInput,
    SelectSiteStatus
  },

  props: {
    // The block's state { on, followSharer, handover, handoverStatus } (siteTransition.initialState), edited in place by this component
    // and by the host (the state is the host's: it also reads it to build the payload).
    value: {
      type: Object,
      required: true
    },

    // 'create' | 'modify'
    mode: {
      type: String,
      default: 'create'
    },

    // The request row's site { status, siteName, handover, handoverStatus, ... }.
    site: {
      type: Object,
      default: null
    },

    // The sharer's own Handover Date (the form's `telco.handover`): the site date follows it (decision 193). Never written here.
    sharerHandover: {
      default: null
    },

    // { handover?, handoverStatus? }: the host's messages, shown on the matching field.
    errors: {
      type: Object,
      default: function () { return {} }
    },

    // The host's saving / frozen state.
    disabled: {
      type: Boolean,
      default: false
    },

    // { title, description } after the API refused the block: replaces the group.
    refusal: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      focused: false,
      live: '',
      ready: false
    }
  },

  computed: {
    statuses () { return STATUSES },

    captionId () { return 'site-transition-caption-' + this._uid },
    errorId () { return 'site-transition-errors-' + this._uid },

    // What the group's screen-reader description says while a field has a message.
    errorText () {
      return [this.errors.handover, this.errors.handoverStatus].filter(Boolean).join(' ')
    },

    siteLabel () {
      return this.site && this.site.siteName ? this.site.siteName : 'The site'
    },

    siteHasDate () {
      return !!this.site && !!ymd(this.site.handover)
    },

    // The status the site already has scheduled (one of the four), or null.
    stored () {
      return storedStatus(this.site)
    },

    same () {
      return this.mode === 'modify' && sameAsSite(this.value, this.site)
    },

    // Modify: the resolved date differs from the date the site already has.
    dateCue () {
      if (this.mode !== 'modify' || !this.siteHasDate || !this.value.handover) return ''

      return ymd(this.value.handover) !== ymd(this.site.handover) ? 'Currently ' + this.formatDate(this.site.handover) + '.' : ''
    },

    statusCue () {
      if (!this.value.handoverStatus || !this.stored) return ''

      if (this.value.handoverStatus === this.stored) return 'From the site record.'

      return this.mode === 'modify' ? 'Currently ' + this.stored + '.' : ''
    },

    // Decision 86, as Step 5 does it: strings compared, never parsed (XOL-414).
    warning () {
      if (!this.value.handover || !this.value.handoverStatus) return null

      return handoverWarning(ymd(this.value.handover), this.value.handoverStatus, appNowDateSql())
        ? handoverWarningCopy(this.site && this.site.siteName, this.value.handoverStatus)
        : null
    }
  },

  methods: {
    // The host's blocked save: focus the GROUP, never a date input (XOL-479), and show the ring (a script focus is not :focus-visible).
    focusGroup () {
      var el = this.$refs.group

      if (!el) return

      this.focused = true
      el.focus()

      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'center' })
      }
    },

    // Ticking copies the sharer's date at once (while following) and pre-fills the stored status. Both happen in the same tick as the
    // tick itself, before the select below is created (XOL-552). Unticking keeps the state, so ticking again restores it. The `follow()`
    // is not redundant with the watcher below: the host rebuilds the state object when the site changes under an unticked block, and
    // that fresh object has not been through the watcher.
    onToggle (on) {
      if (on) {
        if (!this.value.handoverStatus) {
          this.value.handoverStatus = this.stored
        }

        follow(this.value, this.sharerHandover)
      }

      this.$emit('change')
    },

    onFollow (on) {
      if (on) {
        follow(this.value, this.sharerHandover)
        this.live = this.followText()
      } else {
        // UX A1: a Modify's picker starts at the date the site has; otherwise it keeps the date it was following.
        if (this.mode === 'modify' && this.siteHasDate) {
          this.value.handover = ymd(this.site.handover)
        }

        this.live = 'Site handover date can now be picked.'
      }

      this.$emit('change')
    },

    followText () {
      return this.sharerHandover
        ? 'Site handover date set to ' + this.formatDate(this.sharerHandover) + ', the same as the sharer\'s Handover Date.'
        : 'Site handover date is empty until the sharer\'s Handover Date is set.'
    }
  },

  watch: {
    // Every write to the sharer's date (a pick, the XOL-467 auto-fill, save-time normalising, Cancel) moves the site date while it follows.
    sharerHandover: {
      immediate: true,
      handler () {
        follow(this.value, this.sharerHandover)

        // `ready`: the immediate run at creation never announces, even if a ticked state is mounted (a remount must be silent).
        if (this.ready && this.value.on && this.value.followSharer) {
          this.live = this.followText()
        }
      }
    }
  },

  mounted () {
    this.ready = true
  }
}

</script>


<style scoped>
  /* The look of Step 5's handover group (SiteForm.vue .site-form__handover-group; library "Handover group"), as a block and 720px wide. */
  .site-transition {
    display: block;
    max-width: 720px;
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    padding: 8px 12px;
    margin-bottom: 18px;
  }
  .site-transition:focus { outline: none; }
  .site-transition.is-focused,
  .site-transition:focus-visible { outline: 2px solid #409EFF; outline-offset: 2px; }
  /* A set size: a bare <small> inherits the host, and in LicenseeForm it came out at 10.4px, under the 12px hints. 15.36px is what
     SiteForm's Handover caption renders at in Step 5, so the two groups read the same. */
  .site-transition__caption { display: block; font-size: 15.36px; color: #606266; margin-bottom: 4px; }
  /* Indented to line up with the checkbox's label text; readable text (.text-regular's colour), never muted. */
  .site-transition__hint { display: block; font-size: 12px; line-height: 1.45; color: #606266; margin: 2px 0 4px 24px; }
  .site-transition__hint--cue,
  .site-transition__hint--note { margin-left: 0; }
  .site-transition__sub { margin: 6px 0 2px 24px; }
  .site-transition__grid { flex-wrap: wrap; align-items: flex-start; margin-top: 6px; }
  .site-transition__empty { font-size: 14px; color: #606266; }
  .site-transition__warning { margin: 4px 0 8px; }
  .site-transition__refused { max-width: 720px; margin-bottom: 18px; }
  .site-transition__sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

  .site-transition >>> .el-form-item { margin-bottom: 4px; }
  /* A message inside the box (not absolutely positioned under it, over the border). */
  .site-transition >>> .el-form-item__error { position: static; padding-top: 4px; }
  .site-transition >>> .el-select,
  .site-transition >>> .el-date-editor.el-input { width: 100%; }

  @media (max-width: 575px) {
    /* Element's checkbox is nowrap: the main label overflows a 320px frame. */
    .site-transition >>> .el-checkbox,
    .site-transition >>> .el-checkbox__label { white-space: normal; }
    .site-transition >>> .el-checkbox { display: inline-flex; align-items: flex-start; }
    .site-transition__refused { max-width: 100%; }
  }
</style>
